import { SelfContainedCommand, CommandExecutionContext } from '../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import { API_BASE_URL } from '../../../api/config';
import useEntityStore from '../../../stores/entityStore';
import useSystemStore from '../../../stores/systemStore';

export const enableAPIConnectionCommand: SelfContainedCommand = {
  id: 'enable_api_connection',
  description: `Enable an API connection for the current user/org.

For River-managed APIs: Creates an APIConnection entity by copying configuration from the ManagedAPI template.
For user-owned APIs: Activates an existing APIConnection by setting status to 'active'.

**Usage:**
- Managed API: Provide managedApiId (e.g., "managed_cjdropshipping")
- User-owned: Provide connectionId to reactivate

**Examples:**
\`\`\`json
{
  "managedApiId": "managed_cjdropshipping"
}
\`\`\`

\`\`\`json
{
  "connectionId": "apiconn_xyz123"
}
\`\`\``,

  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'managedApiId',
        type: 'string',
        description: 'Managed API ID to enable (e.g., "managed_cjdropshipping"). Use this for River-managed APIs.',
        required: false,
      },
      {
        name: 'connectionId',
        type: 'string',
        description: 'Existing APIConnection ID to reactivate. Use this for user-owned connections.',
        required: false,
      },
      {
        name: 'contextEntity',
        type: 'string',
        description: 'Entity ID to automatically link integration to (e.g., current app_project or website_project). Creates entity_link automatically after enabling.',
        required: false,
      },
      {
        name: 'autoLinkRelation',
        type: 'string',
        description: 'Relationship type for auto-linking (default: "uses_api_connection"). Only used if contextEntity is provided.',
        required: false,
      },
    ],
  },
  
  examples: {
    good: [
      {
        input: {
          managedApiId: 'managed_cjdropshipping',
        },
        expectedOutput: {
          status: 'success',
          message: 'CJDropshipping API enabled',
          format: 'json',
          data: {
            connectionId: 'apiconn_...',
            serviceName: 'CJDropshipping',
            status: 'active',
          },
        },
        description: 'Enable a River-managed API',
      },
      {
        input: {
          connectionId: 'apiconn_xyz123',
        },
        expectedOutput: {
          status: 'success',
          message: 'API connection reactivated',
          format: 'json',
          data: {
            connectionId: 'apiconn_xyz123',
            status: 'active',
          },
        },
        description: 'Reactivate a disabled user-owned connection',
      },
    ],
    bad: [
      {
        input: {},
        error: 'Either managedApiId or connectionId must be provided',
        why: 'No parameters provided',
        fix: 'Provide managedApiId for managed APIs or connectionId for user-owned',
      },
    ],
  },
  
  handler: async (params, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { managedApiId, connectionId, contextEntity, autoLinkRelation } = params;
    
    if (!managedApiId && !connectionId) {
      return createErrorResult(
        'Either managedApiId or connectionId must be provided',
        'Provide managedApiId to enable a River-managed API, or connectionId to reactivate a user-owned connection',
        'validation'
      );
    }
    
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        return createErrorResult('Not authenticated', 'No auth token found', 'permission');
      }
      
      // MODE 1: Enable River-managed API
      if (managedApiId) {
        const orgId = useSystemStore.getState().currentContext?.orgId || null;
        
        const response = await fetch(`${API_BASE_URL}/api-connections/enable-managed`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            managedApiId,
            orgId,
          }),
        });
        
        if (!response.ok) {
          const error = await response.json();
          const detail = error.detail;
          // detail may be an object (e.g. plan upgrade required) or a string
          const message = typeof detail === 'object'
            ? (detail?.message || `${detail?.error || 'Failed to enable managed API'} (requires ${detail?.required_plan || 'upgrade'})`)
            : (detail || 'Failed to enable managed API');
          return createErrorResult(message, JSON.stringify(error), 'execution');
        }
        
        const result = await response.json();
        
        // Fetch the newly created APIConnection entity
        const connectionResponse = await fetch(`${API_BASE_URL}/entities/${result.connection_id}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        
        let newConnection: any = null;
        if (connectionResponse.ok) {
          newConnection = await connectionResponse.json();
          
          // Add to entity store
          const entityStore = useEntityStore.getState();
          entityStore.addEntity(newConnection);
        }
        
        // Auto-link if contextEntity provided
        if (contextEntity) {
          try {
            console.log('[enable_api_connection] Auto-linking to context:', contextEntity);
            
            // Import entityService
            const { entityService } = await import('../../../services/entityService');
            
            // Create entity_link
            await entityService.addLink(
              contextEntity,
              result.connection_id,
              autoLinkRelation || 'uses_api_connection',
              {
                connected_at: new Date().toISOString(),
                auto_linked: true
              }
            );
            
            console.log('[enable_api_connection] ✅ Entity edge created');
            
            // Update project's requiredIntegrations status
            await updateProjectIntegrationStatus(
              contextEntity,
              managedApiId,
              result.connection_id
            );
            
            return createSuccessResult(
              `${newConnection?.name || newConnection?.skeleton?.serviceName || 'Managed API'} enabled and linked`,
              {
                connectionId: result.connection_id,
                serviceName: newConnection?.skeleton?.serviceName,
                status: 'active',
                isManaged: true,
                linkedTo: contextEntity,
                relationshipType: autoLinkRelation || 'uses_api_connection'
              },
              {
                metadata: {
                  affectedEntities: [result.connection_id, contextEntity],
                },
              }
            );
          } catch (linkError: any) {
            console.error('[enable_api_connection] Auto-link failed:', linkError);
            // Still return success (connection created, just linking failed)
          }
        }
        
        if (newConnection) {
          return createSuccessResult(
            `${newConnection.name || newConnection.skeleton?.serviceName} enabled`,
            {
              connectionId: result.connection_id,
              serviceName: newConnection.skeleton?.serviceName,
              status: 'active',
              isManaged: true,
            },
            {
              metadata: {
                affectedEntities: [result.connection_id],
              },
            }
          );
        }
        
        // Connection created but couldn't fetch details
        return createSuccessResult(
          'Managed API enabled',
          {
            connectionId: result.connection_id,
            status: 'active',
            isManaged: true,
          },
          {
            metadata: {
              affectedEntities: [result.connection_id],
            },
          }
        );
      }
      
      // MODE 2: Reactivate user-owned APIConnection
      if (connectionId) {
        const entityStore = useEntityStore.getState();
        const connection = entityStore.entities.find(e => e._id === connectionId);
        
        if (!connection || connection.entityType !== 'APIConnection') {
          return createErrorResult(
            'APIConnection not found',
            `No APIConnection found with ID: ${connectionId}`,
            'validation'
          );
        }
        
        // Update status to active
        await entityStore.actions.updateEntity(connectionId, {
          skeleton: {
            ...connection.skeleton,
            status: 'active',
          },
        });
        
        return createSuccessResult(
          `${connection.name} reactivated`,
          {
            connectionId,
            serviceName: connection.skeleton?.serviceName,
            status: 'active',
            isManaged: false,
          },
          {
            metadata: {
              affectedEntities: [connectionId],
            },
          }
        );
      }
      
      return createErrorResult('Unexpected error', 'No action taken', 'execution');
      
    } catch (error: any) {
      console.error('[enable_api_connection] Error:', error);
      return createErrorResult(
        `Failed to enable API connection: ${error.message}`,
        error,
        'execution'
      );
    }
  },
};

/**
 * Helper function to update project's requiredIntegrations status
 */
async function updateProjectIntegrationStatus(
  projectId: string,
  identifierKey: string,  // serviceName or managedApiId
  connectionId: string
) {
  try {
    const { entityService } = await import('../../../services/entityService');
    const entity = await entityService.get(projectId);
    
    if (!entity) {
      console.warn('[updateProjectIntegrationStatus] Project entity not found:', projectId);
      return;
    }
    
    const requiredIntegrations = entity.skeleton?.requiredIntegrations || [];
    
    // Find and update the integration status
    const updated = requiredIntegrations.map((req: any) => {
      // Match by serviceName or managedApiId
      if (req.serviceName === identifierKey || req.managedApiId === identifierKey) {
        return {
          ...req,
          status: 'connected',
          connectionId
        };
      }
      return req;
    });
    
    await entityService.update(projectId, {
      skeleton: {
        ...entity.skeleton,
        requiredIntegrations: updated
      }
    });
    
    console.log('[updateProjectIntegrationStatus] Updated project integration status');
  } catch (error) {
    console.error('[updateProjectIntegrationStatus] Failed:', error);
    // Non-fatal, just log
  }
}
